import { COMPANY_COLORS } from '@/constants/colors.constant'
import {
  useAttributionDailySummaries,
  useAttributionDateRange,
  useAttributionPeriodLabel,
} from '@/contexts/FAAttributionDataContext/hooks'
import { useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { cardPrefsContextKey, CardProps } from '@/interfaces/raDashboard.interface'
import { putUserPreference } from '@/services/userPreference.service'
import raDashboardDataService, { AttributionTotalReturnsData } from '@/services/report-dashboard/raDashboard.data.service'
import { formatReturnValue } from '@/utils/attributionReturnFormat'
import { dailyReturnTickIndices, mapDailyReturnsWithTimestamp } from '@/utils/dailyReturnUtils'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Menu, MenuItem } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import React, { useEffect, useMemo, useState } from 'react'
import { cardStyle } from '../../../styles/reportDashboardStyles'

dayjs.extend(utc)

export interface DailyReturnPoint {
  date: string
  portfolio: number
  benchmark: number
  active: number
}

enum ChartGraphOptions {
  Curved = 'Curved Lines',
  Straight = 'Straight Lines'
}
const CHART_OPTIONS = Object.values(ChartGraphOptions);

// `seriesKey` is the shared visibility toggle: Active Return Negative/Positive both control the
// same underlying Active Return line, so they share one entry in hiddenSeries.
const LEGEND_ITEMS = [
  { label: 'Active Return Negative', color: COMPANY_COLORS.ERROR, seriesKey: 'Active Return' },
  { label: 'Active Return Positive', color: COMPANY_COLORS.SUCCESS, seriesKey: 'Active Return' },
  { label: 'Portfolio Return', color: COMPANY_COLORS.DARK_BLUE, seriesKey: 'Portfolio Return' },
  { label: 'Benchmark Return', color: COMPANY_COLORS.WARNING, seriesKey: 'Benchmark Return' },
]

// Daily return chart: Portfolio/Benchmark spline lines plus a single Active Return area — green
// above 0, red below 0.
const DailyReturnChart: React.FC<CardProps> = (props) => {
  const theme = useTheme()
  const dailySummaries = useAttributionDailySummaries()
  const periodLabel = useAttributionPeriodLabel()
  const dateRange = useAttributionDateRange()
  const [selectedChart, setSelectedChart] = useState<ChartGraphOptions>(() => {
    const saved = props.cardPrefs?.dailyReturn?.selectedChart
    return saved && CHART_OPTIONS.includes(saved as ChartGraphOptions)
      ? (saved as ChartGraphOptions)
      : ChartGraphOptions.Curved
  });
  const [chartMenuAnchor, setChartMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(
    () => new Set(props.cardPrefs?.dailyReturn?.hiddenSeries ?? []),
  )
  // Current zoom window (null = full range): category indices (drive the axis extremes declaratively so
  // the zoom survives re-renders), display labels, and dates to recompute the window's returns.
  const [zoomedRange, setZoomedRange] = useState<{
    lo: number
    hi: number
    startLabel: string
    endLabel: string
    start: dayjs.Dayjs
    end: dayjs.Dayjs
  } | null>(null)
  // Returns recomputed for the zoomed window via the same API the date picker uses.
  const [rangeReturns, setRangeReturns] = useState<AttributionTotalReturnsData | null>(null)
  const [rangeReturnsLoading, setRangeReturnsLoading] = useState(false)
  const [rangeReturnsError, setRangeReturnsError] = useState(false)

  useEffect(() => {
    const dr = props.cardPrefs?.dailyReturn
    if (!dr) return
    if (CHART_OPTIONS.includes(dr.selectedChart as ChartGraphOptions)) {
      setSelectedChart(dr.selectedChart as ChartGraphOptions)
    }
    setHiddenSeries(new Set(dr.hiddenSeries ?? []))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.cardPrefs])

  const savePrefs = (chart: ChartGraphOptions, hidden: Set<string>) => {
    if (props.cardPrefs?.dashboardName) {
      const newPrefs = {
        ...props.cardPrefs,
        dailyReturn: {
          selectedChart: chart,
          hiddenSeries: [...hidden],
        },
      }
      putUserPreference(
        cardPrefsContextKey(props.cardPrefs.dashboardName),
        newPrefs,
      )
      props.onCardPrefsChange?.(newPrefs)
    }
  }

  const toggleSeries = (label: string) => {
    const next = new Set(hiddenSeries)
    if (next.has(label)) next.delete(label)
    else next.add(label)
    setHiddenSeries(next)
    savePrefs(selectedChart, next)
  }
  // The context cache can hold summaries beyond the selected window (backend returns the full
  // report and the cache merges across runs), so clamp the plotted points to the analyzed range.
  const data = useMemo(() => {
    const mapped = mapDailyReturnsWithTimestamp(dailySummaries ?? undefined)
    if (!dateRange) return mapped
    const beginMs = dateRange.beginDate.startOf('day').valueOf()
    const endMs = dateRange.endDate.endOf('day').valueOf()
    return mapped.filter((p) => p.timestamp >= beginMs && p.timestamp <= endMs)
  }, [dailySummaries, dateRange])
  const hasData = data.length > 0

  // New data resets to the full range (the zoom is by category index), so drop the stale chip.
  useEffect(() => {
    setZoomedRange(null)
  }, [data])

  // Recompute the window's returns via the same total-return API the date picker uses; guard stale responses.
  useEffect(() => {
    if (!zoomedRange) {
      setRangeReturns(null)
      setRangeReturnsLoading(false)
      setRangeReturnsError(false)
      return
    }
    let cancelled = false
    // Clear stale figures so a re-zoom shows '--' until the new window resolves.
    setRangeReturns(null)
    setRangeReturnsLoading(true)
    setRangeReturnsError(false)
    raDashboardDataService
      .getDateRangeTotalReturn(zoomedRange.start, zoomedRange.end, true)
      .then((res) => {
        if (cancelled) return
        // The service resolves (not rejects) on failure, with data null + an error/empty flag.
        setRangeReturnsError(!!res?.error || !!res?.empty || res?.data == null)
        setRangeReturns(res?.data ?? null)
        setRangeReturnsLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setRangeReturnsError(true)
        setRangeReturns(null)
        setRangeReturnsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [zoomedRange])

  const chartOptions = useMemo((): Highcharts.Options => {
    const points = data ?? []
    // Category axis: one slot per date so weekends collapse. Format from the raw timestamp (not the
    // display label) so the labels stay timezone-correct.
    const categories = points.map((d) => dayjs.utc(d.timestamp).format('MM/DD/YYYY'))
    // Single symmetric axis covering all three series.
    const absMax = Math.max(
      0.001,
      ...points.flatMap((d) => [Math.abs(d.portfolio), Math.abs(d.benchmark), Math.abs(d.active)]),
    )
    // Active Return is one line/area: cwan green above 0, cwan red below 0. Highcharts splits at the
    // threshold, so the colour just flips at the crossing with no line drawn along the zero axis.
    // Most opaque at the peak (line), fading out toward the zero axis.
    const greenFill: Highcharts.GradientColorObject = {
      linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
      stops: [
        [0, Highcharts.color(COMPANY_COLORS.SUCCESS).setOpacity(0.65).get('rgba') as string],
        [1, Highcharts.color(COMPANY_COLORS.SUCCESS).setOpacity(0.1).get('rgba') as string],
      ],
    }
    const redFill: Highcharts.GradientColorObject = {
      linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
      stops: [
        [0, Highcharts.color(COMPANY_COLORS.ERROR).setOpacity(0.1).get('rgba') as string],
        [1, Highcharts.color(COMPANY_COLORS.ERROR).setOpacity(0.65).get('rgba') as string],
      ],
    }
    const isCurved = selectedChart === ChartGraphOptions.Curved;
    const areaChartType = isCurved ? 'areaspline' : 'area';
    const chartType = isCurved ? 'spline' : 'line';
    // Active Return Positive/Negative legend entries share one visibility state (see LEGEND_ITEMS).
    const activeReturnHidden = hiddenSeries.has('Active Return')
    const seriesList: Highcharts.SeriesOptionsType[] = [
      {
        // Fill only (no line) — clamped to its own side so its gradient fades within that region.
        type: areaChartType,
        name: 'Active Return Positive',
        data: points.map((d) => Math.max(d.active, 0)),
        color: COMPANY_COLORS.SUCCESS,
        fillColor: greenFill,
        lineWidth: 0,
        threshold: 0,
        yAxis: 0,
        zIndex: 0,
        visible: !activeReturnHidden,
        enableMouseTracking: false,
        marker: { enabled: false },
      },
      {
        type: areaChartType,
        name: 'Active Return Negative',
        data: points.map((d) => Math.min(d.active, 0)),
        color: COMPANY_COLORS.ERROR,
        fillColor: redFill,
        lineWidth: 0,
        threshold: 0,
        yAxis: 0,
        zIndex: 0,
        visible: !activeReturnHidden,
        enableMouseTracking: false,
        marker: { enabled: false },
      },
      {
        // The single Active line: green above 0, red below 0, no line along the zero axis.
        type: chartType,
        name: 'Active Return',
        data: points.map((d) => d.active),
        color: COMPANY_COLORS.SUCCESS,
        negativeColor: COMPANY_COLORS.ERROR,
        lineWidth: 2,
        threshold: 0,
        yAxis: 0,
        zIndex: 1,
        showInLegend: false,
        visible: !activeReturnHidden,
        marker: { enabled: false },
      },
      {
        type: chartType,
        name: 'Portfolio Return',
        data: points.map((d) => d.portfolio),
        color: COMPANY_COLORS.DARK_BLUE,
        yAxis: 0,
        zIndex: 2,
        visible: !hiddenSeries.has('Portfolio Return'),
      },
      {
        type: chartType,
        name: 'Benchmark Return',
        data: points.map((d) => d.benchmark),
        color: COMPANY_COLORS.WARNING,
        yAxis: 0,
        zIndex: 2,
        visible: !hiddenSeries.has('Benchmark Return'),
      },
    ]
    return {
      chart: {
        zooming: { type: 'x' },
        events: {
          // Snap each end of the drag to the nearest day (each day slot spans [i-0.5, i+0.5]) and drive
          // the zoom from state via xAxis.min/max — returning false so Highcharts doesn't also zoom.
          // Reset is the chip's ✕ (there's no native reset button since Highcharts never zooms here).
          selection: function (event: Highcharts.SelectEventObject) {
            const sel = event.xAxis?.[0]
            if (!sel) return undefined
            const clamp = (i: number) => Math.max(0, Math.min(categories.length - 1, i))
            const lo = clamp(Math.round(sel.min))
            const hi = clamp(Math.round(sel.max))
            // Raw timestamps (not the display label) for the API; label as M/D/YYYY like the rest.
            const start = dayjs.utc(points[lo].timestamp)
            const end = dayjs.utc(points[hi].timestamp)
            setZoomedRange({ lo, hi, startLabel: start.format('M/D/YYYY'), endLabel: end.format('M/D/YYYY'), start, end })
            return false
          },
        },
        backgroundColor: theme.cardBackground,
        style: { fontFamily: "'Noto Sans', sans-serif" },
        animation: false,
        spacing: [8, 8, 8, 8],
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        type: 'category',
        categories,
        startOnTick: false,
        endOnTick: false,
        // Declarative zoom: extremes come from state (frame the day slots), so the zoom survives the
        // re-renders from the chip / returns fetch. null = full range.
        min: zoomedRange ? zoomedRange.lo - 0.5 : null,
        max: zoomedRange ? zoomedRange.hi + 0.5 : null,
        // Evenly spaced labels sized to the plot width; recomputes on zoom.
        tickPositioner: function () {
          const lo = Math.max(0, Math.ceil(this.min ?? 0))
          const hi = Math.min(categories.length - 1, Math.floor(this.max ?? categories.length - 1))
          const target = Math.max(2, Math.floor(this.len / 110))
          return dailyReturnTickIndices(lo, hi, target)
        },
        labels: { rotation: 0, style: { color: theme.textPrimary, fontSize: '14px' } },
        lineColor: theme.border,
        tickColor: theme.border,
        gridLineColor: theme.border,
      },
      yAxis: {
        title: { text: 'Return (%)', style: { color: theme.textPrimary, fontSize: '14px' } },
        labels: { format: '{value}%', style: { color: theme.textPrimary, fontSize: '14px' } },
        gridLineColor: theme.border,
        lineColor: theme.border,
        min: -absMax * 1.1,
        max: absMax * 1.1,
      },
      tooltip: {
        shared: true,
        backgroundColor: theme.cardBackground,
        borderColor: theme.border,
        style: { color: theme.textPrimary, fontSize: '14px' },
        headerFormat: '<span style="font-size:12px;font-weight:600">{point.key}</span><br/>',
        pointFormatter: function (this: Highcharts.Point) {
          const dotColor = (this as unknown as { color?: string }).color ?? this.series.color
          const val = this.y == null ? 'N/A' : `${this.y.toFixed(2)}%`
          return `<span style="color:${dotColor}">●</span> ${this.series.name}: <b>${val}</b><br/>`
        },
      },
      plotOptions: {
        spline: { lineWidth: 2 },
        series: {
          states: { inactive: { opacity: 1 } },
          // Show point markers only when there's a single day, otherwise a lone point draws nothing.
          marker: { enabled: points.length === 1, radius: 4 }
        },
      },
      series: seriesList,
    }
  }, [theme, data, selectedChart, hiddenSeries, zoomedRange])

  const dropdownSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    borderRadius: '6px',
    border: `1px solid ${theme.border}`,
    cursor: 'pointer',
    fontSize: '14px',
    color: theme.textPrimary,
    backgroundColor: theme.cardBackground,
    userSelect: 'none' as const,
    whiteSpace: 'nowrap' as const,
  }

  const menuPaperSx = {
    backgroundColor: theme.cardBackground,
    border: `1px solid ${theme.border}`,
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    minWidth: 140,
  }

  return (
    <Stack
      sx={{
        ...cardStyle,
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        padding: '12px',
        boxSizing: 'border-box',
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}
    >
      {/* Header */}
      <Stack
        direction='row'
        justifyContent='space-between'
        sx={{
            borderBottom: `1px solid ${theme.border}`,
            mb: '8px',
            pb: '4px',
        }}
      >
        <Stack>
          <Typography sx={{ fontSize: '14px', fontWeight: 'bold', color: theme.textPrimary }}>
            Daily Return
          </Typography>
          {periodLabel && (
            <Typography sx={{ fontSize: '12px', color: theme.textSecondary, mt: '4px' }}>
              Portfolio vs Benchmark performance from {periodLabel}
            </Typography>
          )}
          {zoomedRange && (
            <Stack sx={{ mt: '6px', alignSelf: 'flex-start' }} spacing={0.5}>
              <Stack
                direction='row'
                alignItems='center'
                spacing={0.5}
                sx={{
                  alignSelf: 'flex-start',
                  padding: '2px 8px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.cardBackground,
                }}
              >
                <Typography sx={{ fontSize: '12px', color: theme.textPrimary }}>
                  {zoomedRange.startLabel} – {zoomedRange.endLabel}
                </Typography>
                <Box
                  component='button'
                  type='button'
                  aria-label='Clear zoom'
                  onClick={() => setZoomedRange(null)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: theme.textSecondary,
                    '&:hover': { color: theme.textPrimary },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </Box>
              </Stack>
              {rangeReturnsError ? (
                <Typography sx={{ fontSize: '12px', color: COMPANY_COLORS.ERROR }}>
                  Couldn&apos;t load returns for this range.
                </Typography>
              ) : (
                <Stack direction='row' flexWrap='wrap' sx={{ gap: '4px 16px' }}>
                  {[
                    { label: 'Portfolio return', value: rangeReturns?.portfolioContribution ?? null, dot: COMPANY_COLORS.DARK_BLUE },
                    { label: 'Benchmark return', value: rangeReturns?.benchmarkContribution ?? null, dot: COMPANY_COLORS.WARNING },
                    { label: 'Active return', value: rangeReturns?.differenceContribution ?? null, dot: null },
                  ].map(({ label, value, dot }) => {
                    // Loading renders the '--' placeholder instead of a value.
                    const { text, sign } = formatReturnValue(value, rangeReturnsLoading)
                    const valueColor = sign === 'positive' ? COMPANY_COLORS.SUCCESS : sign === 'negative' ? COMPANY_COLORS.ERROR : theme.textSecondary
                    return (
                      <Stack key={label} direction='row' alignItems='center' spacing={0.5}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dot ?? valueColor }} />
                        <Typography sx={{ fontSize: '12px', color: theme.textSecondary }}>
                          {label}: <Box component='span' sx={{ color: valueColor, fontWeight: 600 }}>{text}</Box>
                        </Typography>
                      </Stack>
                    )
                  })}
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
        <Stack direction='row' alignItems='center' spacing={1}>
          {/* Chart type dropdown */}
              <Box
                sx={dropdownSx}
                onClick={(e) => setChartMenuAnchor(e.currentTarget)}
              >
                {selectedChart}
                <KeyboardArrowDownIcon sx={{ fontSize: '16px' }} />
              </Box>
              <Menu
                anchorEl={chartMenuAnchor}
                open={Boolean(chartMenuAnchor)}
                onClose={() => setChartMenuAnchor(null)}
                slotProps={{
                  paper: { sx: menuPaperSx }
                }}
              >
                {CHART_OPTIONS.map((opt) => (
                  <MenuItem
                    key={opt}
                    onClick={() => {
                      setSelectedChart(opt)
                      setChartMenuAnchor(null)
                      savePrefs(opt, hiddenSeries)
                    }}
                    sx={{
                      fontSize: '14px',
                      color: theme.textPrimary,
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                      '&:hover': { backgroundColor: theme.border },
                    }}
                  >
                    {opt}
                    {opt === selectedChart && (
                      <Typography component='span' sx={{ fontSize: '14px' }}>
                        ✓
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </Menu>
        </Stack>
      </Stack>
      {/* Content */}
      {hasData ? (
        <Stack sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            containerProps={{ style: { height: 'calc(100% - 28px)', width: '100%' } }}
          />
          <Stack direction='row' sx={{ justifyContent: 'center', flexWrap: 'wrap', gap: '16px', pt: '4px' }}>
            {LEGEND_ITEMS.map((item) => {
              const isHidden = hiddenSeries.has(item.seriesKey)
              return (
                <Stack
                  key={item.label}
                  direction='row'
                  onClick={() => toggleSeries(item.seriesKey)}
                  sx={{
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    opacity: isHidden ? 0.4 : 1,
                    userSelect: 'none',
                  }}
                >
                  <Box sx={{ width: 16, height: 4, borderRadius: '2px', backgroundColor: item.color }} />
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: theme.textPrimary,
                      textDecoration: isHidden ? 'line-through' : 'none',
                    }}
                  >
                    {item.label}
                  </Typography>
                </Stack>
              )
            })}
          </Stack>
        </Stack>
      ) : (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: theme.textPrimary }}>
            No daily return data
          </Typography>
          <Typography sx={{ fontSize: '13px', color: theme.textSecondary, mt: '4px' }}>
            Run analysis for the selected portfolio and date range to view daily returns.
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}

export default DailyReturnChart
